/**
 * googleTracking.ts — single source of truth for Google measurement.
 *
 * Owns:
 *   - GA4 + Google Ads + GTM IDs and labels (env-first, remote-config-fallback)
 *   - Consent Mode v2 init / update
 *   - GA4 page_view (SPA, deduped — first load is not double-counted)
 *   - GA4 custom events
 *   - Google Ads conversions (lead / quote / estimate / phone / email)
 *   - dataLayer pushes (GTM)
 *   - Optional Enhanced Conversions user_data push (guarded by env + consent)
 *   - Debug logger
 *
 * Modes (VITE_GOOGLE_TRACKING_MODE):
 *   - "direct" (default): inject gtag.js, config GA4 with send_page_view:false,
 *     config Ads if present. SPA page_views via trackGooglePageView.
 *   - "gtm": inject GTM web container only. No direct GA4 config; GA4 + Ads tags
 *     live inside GTM. page_view + conversions pushed to dataLayer.
 *   - "hybrid": inject GTM AND direct gtag for Ads. GA4 page_views via GTM ONLY
 *     (direct GA4 page_view is suppressed to prevent double counting).
 *
 * Never injects the server GTM container (VITE_GTM_SERVER_CONTAINER_ID) as a
 * browser script — it's documentation only.
 */

import { getStoredAttribution, getGoogleClickIds, getAttributionForEventParams } from './marketingAttribution';
import type { BrandAnalyticsConfig } from './brandAnalytics';
import { shouldEnableProductionTracking } from './trackingEnvironment';

// Idempotent global initialization flag. Exposed on window so repeated
// TrackingProvider mounts (React Strict Mode, HMR, remote-config reinit)
// cannot load a second gtag.js or issue duplicate config commands.
const INIT_FLAG = '__OBC_GOOGLE_TRACKING_INITIALIZED__';

// Per-brand attribution auto-stamped on every GA4/GTM event (set by
// applyBrandAnalytics from TrackingProvider). Filter inside GA4/Looker/Ads.
let brandAttribution: Record<string, string> = {};
let brandEnabled = true;

export function applyBrandAnalytics(cfg: BrandAnalyticsConfig): void {
  brandEnabled = cfg.enabled;
  brandAttribution = {
    source_brand: cfg.attribution.sourceBrand,
    microsite_topic: cfg.attribution.micrositeTopic,
    parent_brand: cfg.attribution.parentBrand,
    license: cfg.attribution.license,
  };
  // Brand wins over env for IDs/labels/mode (env still wins if brand left blank).
  if (cfg.mode) config.mode = cfg.mode;
  if (cfg.tagIds.ga4MeasurementId && cfg.platforms.ga4)
    config.ga4MeasurementId = sanitizeId(cfg.tagIds.ga4MeasurementId);
  if (cfg.tagIds.googleAdsId && cfg.platforms.googleAds)
    config.googleAdsId = sanitizeId(cfg.tagIds.googleAdsId);
  if (cfg.tagIds.gtmWebContainerId && cfg.platforms.gtm)
    config.gtmWebContainerId = sanitizeId(cfg.tagIds.gtmWebContainerId);
  if (cfg.tagIds.gtmServerContainerId)
    config.gtmServerContainerId = sanitizeId(cfg.tagIds.gtmServerContainerId);
  // Disabled platforms blank their IDs so init won't load tags.
  if (!cfg.platforms.ga4) config.ga4MeasurementId = '';
  if (!cfg.platforms.googleAds) config.googleAdsId = '';
  if (!cfg.platforms.gtm) config.gtmWebContainerId = '';

  for (const k of ['lead', 'quote', 'estimate', 'phone', 'email'] as const) {
    if (cfg.conversionLabels[k]) config.labels[k] = cfg.conversionLabels[k];
  }
  config.enableEnhancedConversions = cfg.enhancedConversionsEnabled;
  if (cfg.debug) config.debug = true;
}

export function isBrandTrackingEnabled(): boolean {
  return brandEnabled;
}

// ---------- Config ------------------------------------------------------------

export type TrackingMode = 'direct' | 'gtm' | 'hybrid';

interface GoogleTrackingConfig {
  ga4MeasurementId: string;
  gtmWebContainerId: string;
  gtmServerContainerId: string;
  googleAdsId: string;
  labels: {
    lead: string;
    quote: string;
    estimate: string;
    phone: string;
    email: string;
  };
  mode: TrackingMode;
  debug: boolean;
  enableEnhancedConversions: boolean;
  enableLeadAsPurchase: boolean;
}

const env = (k: string, fallback = ''): string => {
  const v = (import.meta.env as Record<string, string | undefined>)[k];
  return typeof v === 'string' ? v : fallback;
};

const sanitizeId = (v: string): string => (v && !v.includes('XXX') ? v.trim() : '');

const initialMode = ((): TrackingMode => {
  const raw = env('VITE_GOOGLE_TRACKING_MODE', 'direct').toLowerCase();
  return raw === 'gtm' || raw === 'hybrid' ? raw : 'direct';
})();

const config: GoogleTrackingConfig = {
  ga4MeasurementId: sanitizeId(env('VITE_GA_MEASUREMENT_ID', 'G-KBZ32H7S4P')),
  gtmWebContainerId: sanitizeId(env('VITE_GTM_WEB_CONTAINER_ID')),
  gtmServerContainerId: sanitizeId(env('VITE_GTM_SERVER_CONTAINER_ID')),
  googleAdsId: sanitizeId(env('VITE_GOOGLE_ADS_ID')),
  labels: {
    lead: env('VITE_GOOGLE_ADS_LEAD_CONVERSION_LABEL').trim(),
    quote: env('VITE_GOOGLE_ADS_QUOTE_CONVERSION_LABEL').trim(),
    estimate: env('VITE_GOOGLE_ADS_ESTIMATE_CONVERSION_LABEL').trim(),
    phone: env('VITE_GOOGLE_ADS_PHONE_CONVERSION_LABEL').trim(),
    email: env('VITE_GOOGLE_ADS_EMAIL_CONVERSION_LABEL').trim(),
  },
  mode: initialMode,
  debug: env('VITE_GOOGLE_TRACKING_DEBUG', 'false').toLowerCase() === 'true',
  enableEnhancedConversions:
    env('VITE_ENABLE_GOOGLE_ENHANCED_CONVERSIONS', 'false').toLowerCase() === 'true',
  enableLeadAsPurchase:
    env('VITE_ENABLE_LEAD_AS_PURCHASE', 'false').toLowerCase() === 'true',
};

/** Allow Growth Hub remote config to fill blanks — never overwrite a real env value. */
export function applyRemoteConfig(remote: {
  ga4_measurement_id?: string | null;
  google_ads_id?: string | null;
  google_ads_conversion_label?: string | null;
  gtm_id?: string | null;
}) {
  if (!config.ga4MeasurementId && remote.ga4_measurement_id) {
    config.ga4MeasurementId = sanitizeId(remote.ga4_measurement_id);
  }
  if (!config.googleAdsId && remote.google_ads_id) {
    config.googleAdsId = sanitizeId(remote.google_ads_id);
  }
  // Generic Ads label from remote config feeds the "lead" slot if no env was set.
  if (!config.labels.lead && remote.google_ads_conversion_label) {
    config.labels.lead = remote.google_ads_conversion_label.trim();
  }
  if (!config.gtmWebContainerId && remote.gtm_id) {
    // Only treat remote gtm_id as a web container when it isn't the server container.
    const id = sanitizeId(remote.gtm_id);
    if (id && id !== config.gtmServerContainerId) config.gtmWebContainerId = id;
  }
  debug('remote-config applied', { config: getRedactedConfig() });
  markConfigReady();
}

// ---------- Debug -------------------------------------------------------------

function debug(...args: unknown[]) {
  if (!config.debug) return;
  // eslint-disable-next-line no-console
  console.info('[googleTracking]', ...args);
}

function getRedactedConfig() {
  return {
    mode: config.mode,
    ga4: config.ga4MeasurementId || '(missing)',
    ads: config.googleAdsId || '(missing)',
    gtmWeb: config.gtmWebContainerId || '(missing)',
    gtmServer: config.gtmServerContainerId
      ? `${config.gtmServerContainerId} (documentation only — never browser-loaded)`
      : '(missing)',
    labels: {
      lead: config.labels.lead ? '✓' : '(missing)',
      quote: config.labels.quote ? '✓' : '(missing)',
      estimate: config.labels.estimate ? '✓' : '(missing)',
      phone: config.labels.phone ? '✓' : '(missing)',
      email: config.labels.email ? '✓' : '(missing)',
    },
    debug: config.debug,
    enhancedConversions: config.enableEnhancedConversions,
    leadAsPurchase: config.enableLeadAsPurchase,
  };
}

export function getGoogleTrackingConfig() {
  return { ...config };
}

// ---------- Consent Mode v2 ---------------------------------------------------

export type ConsentState = {
  analytics_storage: 'granted' | 'denied';
  ad_storage: 'granted' | 'denied';
  ad_user_data: 'granted' | 'denied';
  ad_personalization: 'granted' | 'denied';
  functionality_storage: 'granted' | 'denied';
  personalization_storage: 'granted' | 'denied';
  security_storage: 'granted' | 'denied';
};

// Policy: opt-in Consent Mode v2. Everything advertising/analytics defaults to
// DENIED. functionality/security storage remains granted so the site works.
// The CookieConsent banner + useConsentMode call updateGoogleConsent() to
// upgrade after visitor choice. This matches the defaults hard-coded in
// index.html so the pre-React state and the React state agree.
export const DEFAULT_DENIED_CONSENT: ConsentState = {
  analytics_storage: 'denied',
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
  functionality_storage: 'granted',
  personalization_storage: 'denied',
  security_storage: 'granted',
};

// Legacy export kept for callers that historically imported it. Points at the
// same "denied" object — no code path grants without a real consent update.
export const DEFAULT_GRANTED_CONSENT: ConsentState = { ...DEFAULT_DENIED_CONSENT };

let currentConsent: ConsentState = { ...DEFAULT_DENIED_CONSENT };

function ensureGtagShim() {
  const w = window as any;
  w.dataLayer = w.dataLayer || [];
  if (typeof w.gtag !== 'function') {
    w.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      w.dataLayer.push(arguments);
    };
  }
}

export function initializeConsentMode(initial: Partial<ConsentState> = {}): void {
  if (typeof window === 'undefined') return;
  ensureGtagShim();
  currentConsent = { ...DEFAULT_DENIED_CONSENT, ...initial };
  (window as any).gtag('consent', 'default', { ...currentConsent });
  debug('consent default', currentConsent);
}

export function updateGoogleConsent(next: Partial<ConsentState>): void {
  if (typeof window === 'undefined') return;
  ensureGtagShim();
  currentConsent = { ...currentConsent, ...next };
  (window as any).gtag('consent', 'update', { ...currentConsent });
  debug('consent update', currentConsent);
}

export function getConsentState(): ConsentState {
  return { ...currentConsent };
}

// ---------- Init: load tags ---------------------------------------------------

let initialized = false;

export function initializeGoogleTracking(): void {
  if (typeof window === 'undefined') return;
  const w = window as any;
  if (initialized || w[INIT_FLAG]) return;
  if (!shouldEnableProductionTracking()) {
    debug('init skipped:', w.__OBC_TRACKING_DISABLED_REASON__ || 'non-production host');
    return;
  }
  initialized = true;
  w[INIT_FLAG] = true;
  ensureGtagShim();

  // gtag('js', new Date()) — once.
  w.gtag('js', new Date());

  const mode = config.mode;
  const wantGtm = mode === 'gtm' || mode === 'hybrid';
  const wantDirectGa4 = mode === 'direct'; // hybrid relies on GTM for GA4
  const wantDirectAds = mode === 'direct' || mode === 'hybrid';

  // ---- GTM web container -----------------------------------------------------
  if (wantGtm && config.gtmWebContainerId) {
    if (config.gtmWebContainerId === config.gtmServerContainerId) {
      debug('skip GTM: web ID equals server ID — refusing to load server container in browser');
    } else if (!document.querySelector(`script[data-gtm-id="${config.gtmWebContainerId}"]`)) {
      (window as any).dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
      const s = document.createElement('script');
      s.async = true;
      s.src = `https://www.googletagmanager.com/gtm.js?id=${config.gtmWebContainerId}`;
      s.setAttribute('data-gtm-id', config.gtmWebContainerId);
      document.head.appendChild(s);

      if (
        !document.querySelector(
          `noscript iframe[src*="googletagmanager.com/ns.html?id=${config.gtmWebContainerId}"]`,
        )
      ) {
        const ns = document.createElement('noscript');
        const f = document.createElement('iframe');
        f.src = `https://www.googletagmanager.com/ns.html?id=${config.gtmWebContainerId}`;
        f.height = '0';
        f.width = '0';
        f.style.display = 'none';
        f.style.visibility = 'hidden';
        ns.appendChild(f);
        document.body.appendChild(ns);
      }
      debug('GTM loaded', config.gtmWebContainerId);
    }
  }

  // ---- gtag.js (direct/hybrid) ----------------------------------------------
  // Only inject gtag.js if we have something to configure directly.
  const needGtagLoader = (wantDirectGa4 && config.ga4MeasurementId) || (wantDirectAds && config.googleAdsId);
  if (needGtagLoader) {
    const tagId = config.ga4MeasurementId || config.googleAdsId;
    if (!document.querySelector(`script[src*="googletagmanager.com/gtag/js?id=${tagId}"]`)) {
      const s = document.createElement('script');
      s.async = true;
      s.src = `https://www.googletagmanager.com/gtag/js?id=${tagId}`;
      document.head.appendChild(s);
    }
  }

  // GA4 config — only in direct mode; hybrid lets GTM own GA4 to avoid duplicates.
  if (wantDirectGa4 && config.ga4MeasurementId) {
    (window as any).gtag('config', config.ga4MeasurementId, {
      send_page_view: false, // SPA: we fire page_view manually per route
    });
    debug('GA4 configured (direct)', config.ga4MeasurementId);
  }

  // Google Ads config — direct or hybrid; safe to coexist with GTM's Ads tag
  // because no automatic conversion fires here.
  if (wantDirectAds && config.googleAdsId) {
    (window as any).gtag('config', config.googleAdsId);
    debug('Google Ads configured', config.googleAdsId);
  }

  if (config.debug) {
    // eslint-disable-next-line no-console
    console.table(getRedactedConfig());
  }

  markConfigReady();
}

// ---------- Page view ---------------------------------------------------------

let firstPageViewSent = false;
let lastPageViewPath = '';
let lastInternalPath = ''; // for page_referrer of NEXT navigation

// Query-string params that must never leak into GA4 (customer-entered PII).
const SENSITIVE_QUERY_PARAMS = new Set([
  'email', 'e', 'phone', 'p', 'name', 'first_name', 'last_name',
  'address', 'street', 'zip', 'zipcode',
]);

function sanitizePagePath(rawPath: string): string {
  const qi = rawPath.indexOf('?');
  if (qi === -1) return rawPath;
  const base = rawPath.slice(0, qi);
  try {
    const params = new URLSearchParams(rawPath.slice(qi + 1));
    let mutated = false;
    for (const k of Array.from(params.keys())) {
      if (SENSITIVE_QUERY_PARAMS.has(k.toLowerCase())) {
        params.delete(k);
        mutated = true;
      }
    }
    if (!mutated) return rawPath;
    const s = params.toString();
    return s ? `${base}?${s}` : base;
  } catch {
    return base;
  }
}

interface PageViewArgs {
  page_path: string;
  page_title?: string;
  page_location?: string;
}

export function trackGooglePageView(args: PageViewArgs): void {
  if (typeof window === 'undefined') return;
  if (!shouldEnableProductionTracking()) return;
  const path = sanitizePagePath(args.page_path);
  if (path === lastPageViewPath && firstPageViewSent) {
    // Same path twice in a row (StrictMode etc.) — skip.
    return;
  }
  const referrer = firstPageViewSent
    ? (lastInternalPath ? window.location.origin + lastInternalPath : document.referrer || undefined)
    : (document.referrer || undefined);
  lastInternalPath = lastPageViewPath;
  lastPageViewPath = path;
  firstPageViewSent = true;

  const location = args.page_location || window.location.href;
  const params = {
    page_path: path,
    page_title: args.page_title || (typeof document !== 'undefined' ? document.title : undefined),
    page_location: sanitizePagePath(location),
    page_referrer: referrer,
    site_domain: window.location.hostname,
    ...brandAttribution,
  };

  if (config.mode === 'direct' && config.ga4MeasurementId) {
    (window as any).gtag?.('event', 'page_view', { ...params, send_to: config.ga4MeasurementId });
    debug('page_view (direct)', path);
  }

  pushDataLayerEvent('page_view', params);
}

// ---------- Generic event -----------------------------------------------------

export function trackGoogleEvent(name: string, params: Record<string, unknown> = {}): void {
  if (typeof window === 'undefined') return;
  if (!shouldEnableProductionTracking()) return;
  const enriched = {
    ...params,
    site_domain: window.location.hostname,
    ...brandAttribution,
  };
  if (config.mode === 'direct' && config.ga4MeasurementId) {
    (window as any).gtag?.('event', name, { ...enriched, send_to: config.ga4MeasurementId });
  }
  pushDataLayerEvent(name, enriched);
}

// ---------- Funnel events (patch: reclassified per lead quality) --------------
// Once-per-action guards keep StrictMode / accidental re-invocation from
// double-firing measurement events.
const _firedOnce: Set<string> = new Set();
function _fireOnce(key: string, cb: () => void): void {
  if (_firedOnce.has(key)) return;
  _firedOnce.add(key);
  cb();
}

/** GA4 only. First meaningful interaction with Quick Estimate. */
export function trackEstimateStart(params: Record<string, unknown> = {}): void {
  _fireOnce(`estimate_start:${params.session_id || sessionKey()}`, () =>
    trackGoogleEvent('estimate_start', params),
  );
}

/** GA4 only. Fires after Quick Estimate backend submission succeeds. */
export function trackEstimateComplete(params: Record<string, unknown> = {}): void {
  const k = `estimate_complete:${params.event_id || params.session_id || sessionKey()}`;
  _fireOnce(k, () => trackGoogleEvent('estimate_complete', params));
}

/** GA4 only. Visitor requested visualizer generation after completing inputs. */
export function trackVisualizerStart(params: Record<string, unknown> = {}): void {
  const k = `visualizer_start:${params.session_id || sessionKey()}`;
  _fireOnce(k, () => trackGoogleEvent('visualizer_start', params));
}

/** GA4 only. Visualization successfully generated. Not a primary lead. */
export function trackVisualizerComplete(params: Record<string, unknown> = {}): void {
  const k = `visualizer_complete:${params.event_id || params.session_id || sessionKey()}`;
  _fireOnce(k, () => trackGoogleEvent('visualizer_complete', params));
}

/** Lead-magnet / content download. GA4 only; NEVER fires the primary Ads lead. */
export function trackLeadMagnetSubmit(params: Record<string, unknown> = {}): void {
  const k = `lead_magnet_submit:${params.event_id || params.form_name || 'unknown'}`;
  _fireOnce(k, () => trackGoogleEvent('lead_magnet_submit', params));
}

/**
 * form_error — safe subset only. Never accept raw exception text, stack
 * traces, or customer-entered values. Only categorical strings.
 */
export function trackFormError(params: {
  form_name: string;
  error_type: string; // e.g. "validation" | "network" | "server"
  current_step?: string | number;
  page_path?: string;
}): void {
  const safe = {
    form_name: String(params.form_name || 'unknown').slice(0, 60),
    error_type: String(params.error_type || 'unknown').slice(0, 40),
    current_step: params.current_step,
    page_path: params.page_path || window.location.pathname,
  };
  trackGoogleEvent('form_error', safe);
}

function sessionKey(): string {
  try {
    return sessionStorage.getItem('obc_session_id') || '';
  } catch {
    return '';
  }
}



// ---------- dataLayer ---------------------------------------------------------

export function pushDataLayerEvent(eventName: string, params: Record<string, unknown> = {}): void {
  if (typeof window === 'undefined') return;
  const w = window as any;
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push({ event: eventName, ...stripPII(params) });
}

/** Defensive PII stripper for dataLayer payloads. */
function stripPII<T extends Record<string, unknown>>(obj: T): T {
  const out: Record<string, unknown> = { ...obj };
  for (const k of ['email', 'phone', 'phone_number', 'name', 'first_name', 'last_name', 'address']) {
    if (k in out) delete out[k];
  }
  return out as T;
}

// ---------- Conversions -------------------------------------------------------

export interface ConversionPayload {
  event_id?: string;
  value?: number;
  currency?: string;
  form_id?: string;
  form_name?: string;
  source_page?: string;
  service_type?: string;
  city?: string;
  page_path?: string;
  page_location?: string;
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  visitor_id?: string;
  session_id?: string;
  destination_url?: string;
  event_callback?: () => void;
  event_timeout?: number;
  /** For debug helper — when true, will not fire Ads in production unless ?ads_test=1 */
  debug_mode?: boolean;
  [k: string]: unknown;
}

type ConversionKind = 'lead' | 'quote' | 'estimate' | 'phone' | 'email';

function resolveLabel(kind: ConversionKind): string {
  // quote/estimate fall back to the generic lead label.
  // Phone falls back to the lead label so call clicks are still reported when
  // Google Ads only has one Submit Lead action configured. Email stays separate.
  if (kind === 'quote') return config.labels.quote || config.labels.lead;
  if (kind === 'estimate') return config.labels.estimate || config.labels.lead;
  if (kind === 'phone') return config.labels.phone || config.labels.lead;
  return config.labels[kind] || '';
}

const recentAdsConversions = new Map<string, number>();

function fireGoogleAdsConversion(kind: ConversionKind, payload: ConversionPayload): boolean {
  if (typeof window === 'undefined') return false;
  if (!shouldEnableProductionTracking()) return false;
  if (!config.googleAdsId) {
    debug(`ads ${kind} skipped: no VITE_GOOGLE_ADS_ID`);
    return false;
  }
  const label = resolveLabel(kind);
  if (!label) {
    debug(`ads ${kind} skipped: no conversion label configured`);
    return false;
  }

  // Test/debug events: never fire to live Ads unless explicit opt-in via ?ads_test=1.
  if (payload.debug_mode) {
    const allow =
      typeof window !== 'undefined' &&
      new URLSearchParams(window.location.search).get('ads_test') === '1';
    if (!allow) {
      debug(`ads ${kind} suppressed (debug_mode, no ?ads_test=1)`);
      return false;
    }
  }

  const dedupeKey = payload.event_id
    ? `${kind}:${label}:${payload.event_id}`
    : kind === 'phone' || kind === 'email'
      ? `${kind}:${label}:${payload.page_path || window.location.pathname}`
      : `${kind}:${label}:${payload.destination_url || payload.page_path || window.location.pathname}:${payload.form_id || ''}`;
  const now = Date.now();
  const last = recentAdsConversions.get(dedupeKey) || 0;
  if (now - last < 1200) {
    debug(`ads ${kind} skipped: duplicate click`, { label });
    return false;
  }
  recentAdsConversions.set(dedupeKey, now);

  const ids = getGoogleClickIds();
  const eventParams: Record<string, unknown> = {
    send_to: `${config.googleAdsId}/${label}`,
    value: typeof payload.value === 'number' ? payload.value : 0,
    currency: payload.currency || 'USD',
  };
  if (payload.event_id) eventParams.transaction_id = payload.event_id;
  if (ids.gclid) eventParams.gclid = ids.gclid;
  if (ids.gbraid) eventParams.gbraid = ids.gbraid;
  if (ids.wbraid) eventParams.wbraid = ids.wbraid;

  if (config.mode === 'gtm') {
    // GTM owns Ads in pure gtm mode — push and let the container fire.
    pushDataLayerEvent(`ads_${kind}_conversion`, {
      ...eventParams,
      ...(payload.event_callback ? { eventCallback: payload.event_callback } : {}),
      ...(payload.event_timeout ? { eventTimeout: payload.event_timeout } : {}),
    });
  } else {
    (window as any).gtag?.('event', 'conversion', {
      ...eventParams,
      ...(payload.event_callback ? { event_callback: payload.event_callback } : {}),
      ...(payload.event_timeout ? { event_timeout: payload.event_timeout } : {}),
    });
  }
  debug(`ads ${kind} fired`, { value: eventParams.value, label });
  return true;
}

function fireGenerateLead(kind: ConversionKind, payload: ConversionPayload) {
  if (!shouldEnableProductionTracking()) return;
  const attribution = getAttributionForEventParams();
  const params: Record<string, unknown> = {
    ...attribution,
    value: typeof payload.value === 'number' ? payload.value : 0,
    currency: payload.currency || 'USD',
    form_id: payload.form_id,
    form_name: payload.form_name,
    source_page: payload.source_page || (typeof window !== 'undefined' ? window.location.pathname : undefined),
    service_type: payload.service_type,
    city: payload.city,
    lead_kind: kind,
  };
  if (payload.event_id) params.event_id = params.transaction_id = payload.event_id;
  if (config.mode === 'direct' && config.ga4MeasurementId) {
    (window as any).gtag?.('event', 'generate_lead', { ...params, send_to: config.ga4MeasurementId });
  }
  pushDataLayerEvent('generate_lead', params);

  if (config.enableLeadAsPurchase) {
    // Optional legacy purchase emulation for downstream platforms that need it.
    const purchase = {
      ...params,
      transaction_id: payload.event_id || `lead-${Date.now()}`,
    };
    if (config.mode === 'direct' && config.ga4MeasurementId) {
      (window as any).gtag?.('event', 'purchase', { ...purchase, send_to: config.ga4MeasurementId });
    }
    pushDataLayerEvent('purchase', purchase);
  }
}

export function trackGoogleLeadConversion(payload: ConversionPayload): void {
  fireGenerateLead('lead', payload);
  fireGoogleAdsConversion('lead', payload);
}

export function trackGoogleQuoteConversion(payload: ConversionPayload): void {
  fireGenerateLead('quote', payload);
  fireGoogleAdsConversion('quote', payload);
  pushDataLayerEvent('quote_form_submit', stripPII(payload));
}

export function trackGoogleEstimateConversion(payload: ConversionPayload): void {
  fireGenerateLead('estimate', payload);
  fireGoogleAdsConversion('estimate', payload);
  pushDataLayerEvent('quick_estimate_submit', stripPII(payload));
}

export function trackGooglePhoneConversion(payload: ConversionPayload): boolean {
  trackGoogleEvent('contact', {
    method: 'phone',
    link_location: payload.form_id || payload.form_name,
    page_path: payload.page_path,
  });
  pushDataLayerEvent('phone_click', stripPII(payload));
  return fireGoogleAdsConversion('phone', payload);
}

export function trackGoogleEmailConversion(payload: ConversionPayload): boolean {
  trackGoogleEvent('contact', {
    method: 'email',
    link_location: payload.form_id || payload.form_name,
    page_path: payload.page_path,
  });
  pushDataLayerEvent('email_click', stripPII(payload));
  return fireGoogleAdsConversion('email', payload);
}

// ---------- Identifiers helper -----------------------------------------------

export function getGoogleClientIdentifiers(): {
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  attribution: ReturnType<typeof getStoredAttribution>;
} {
  return {
    ...getGoogleClickIds(),
    attribution: getStoredAttribution(),
  };
}

// ---------- Enhanced Conversions (optional, guarded) -------------------------

export interface EnhancedConversionUserData {
  email?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
}

/**
 * Push hashed user-provided data for Google Ads Enhanced Conversions.
 * Gated by: VITE_ENABLE_GOOGLE_ENHANCED_CONVERSIONS=true AND ad_user_data='granted'
 * AND a configured Google Ads ID.
 *
 * Implementation lives in src/lib/enhancedConversions.ts (SHA-256 + gtag/dataLayer).
 * Raw PII is never pushed to dataLayer.
 */
export async function setGoogleEnhancedConversionUserData(
  data: EnhancedConversionUserData,
): Promise<void> {
  if (!shouldEnableProductionTracking()) return;
  if (!config.enableEnhancedConversions) {
    debug('enhanced conversions disabled (VITE_ENABLE_GOOGLE_ENHANCED_CONVERSIONS=false)');
    return;
  }
  if (!config.googleAdsId) {
    debug('enhanced conversions skipped: no VITE_GOOGLE_ADS_ID');
    return;
  }
  if (currentConsent.ad_user_data !== 'granted') {
    debug('enhanced conversions skipped: ad_user_data not granted');
    return;
  }
  if (!data.email && !data.phone) {
    debug('enhanced conversions skipped: need email or phone');
    return;
  }
  try {
    const { pushEnhancedConversionsUserData } = await import('./enhancedConversions');
    await pushEnhancedConversionsUserData({
      email: data.email,
      phone: data.phone,
      firstName: data.firstName,
      lastName: data.lastName,
      city: data.city,
      state: data.state,
      zip: data.zip,
      country: data.country,
    });
    debug('enhanced conversions user_data pushed');
  } catch (e) {
    debug('enhanced conversions push failed', e);
  }
}

// ---------- Backwards-compat: legacy global helper ---------------------------

type LegacyConversionType = 'lead' | 'quote' | 'estimate' | 'phone' | 'email';

/**
 * Install legacy window.obcTrackConversion so any older callers keep working.
 * Supports an optional conversionType so /thank-you and similar landings can
 * route to the correct Ads conversion label. New code should call
 * trackGoogleLeadConversion / trackGoogleQuoteConversion / etc. directly.
 */
export function installLegacyGlobals(): void {
  if (typeof window === 'undefined') return;
  (window as any).obcTrackConversion = (
    value: number = 0,
    extras: Record<string, unknown> = {},
    conversionType: LegacyConversionType = 'lead',
  ) => {
    const payload = { value, ...extras } as ConversionPayload;
    switch (conversionType) {
      case 'quote':
        trackGoogleQuoteConversion(payload);
        break;
      case 'estimate':
        trackGoogleEstimateConversion(payload);
        break;
      case 'phone':
        trackGooglePhoneConversion(payload);
        break;
      case 'email':
        trackGoogleEmailConversion(payload);
        break;
      case 'lead':
      default:
        trackGoogleLeadConversion(payload);
    }
  };
  markConfigReady();
}

/** Set by init + remote-config so callers can detect when Ads config is loaded. */
export function markConfigReady(): void {
  if (typeof window === 'undefined') return;
  (window as any).__obcGoogleConfigReady = Boolean(
    config.ga4MeasurementId || config.googleAdsId || config.gtmWebContainerId,
  );
}

